import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../components/AuthContext.jsx";
import { landingApi } from "../api/landingApi.js";
import { useTranslation } from "react-i18next";
import RemoveBtn from "../components/RemoveBtn.jsx";
import { gender } from "../components/MockData.js";

const CustomerRegistration = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Tab state: 'new' or 'existing'
  const [activeTab, setActiveTab] = useState("new");

  const [formData, setFormData] = React.useState({
    username: "",
    email: "",
    phone: "",
    gender: "",
    township_id: "",
    district_id: "",
    nationality: "",
    password: "",
    password_confirmation: "",
    date_of_birth: "",
    refer_code: "",
  });

  // Existing user login state
  const [loginData, setLoginData] = useState({
    phone: "",
    password: "",
  });
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);

  // Membership state
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [membershipError, setMembershipError] = useState("");
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isMembershipModalOpen, setIsMembershipModalOpen] = useState(false);

  const membershipPlans = [
    {
      id: "gold",
      name: "Gold Member (Annual)",
      price: 280000,
      discount: "10%",
      validFrom: "Jun 10, 2024",
      validTo: "Jun 09, 2025",
      description: "Annual subscription with 10% discount at partner shops",
    },
    {
      id: "silver",
      name: "Silver Member (Semi-Annual)",
      price: 150000,
      discount: "6%",
      validFrom: "Jun 10, 2024",
      validTo: "Dec 09, 2024",
      description: "Semi-annual subscription with 6% discount at partner shops",
    },
  ];

  // Get refer_code from URL
  const params = new URLSearchParams(window.location.search);
  const referCode = params.get("refer_code");

  const { authToken } = useAuth();
  const [error, setError] = React.useState("");
  const [districts, setDistricts] = React.useState([]);
  const [townships, setTownships] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [nationality, setNationality] = useState([]);

  const [savedPaymentMethod, setSavedPaymentMethod] = useState(() => {
    return localStorage.getItem("selectedPaymentMethod") || "international";
  });

  const usernameRef = React.useRef(null);
  const emailRef = React.useRef(null);
  const phoneRef = React.useRef(null);
  const passwordRef = React.useRef(null);
  const passwordConfirmRef = React.useRef(null);

  // Helper function to format date for input (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    const parts = dateString.split(/[-/]/);
    if (parts.length === 3) {
      const day = parseInt(parts[0]);
      const month = parseInt(parts[1]);
      const year = parseInt(parts[2]);

      if (!isNaN(day) && !isNaN(month) && !isNaN(year) && year > 1900) {
        return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      }

      const month2 = parseInt(parts[0]);
      const day2 = parseInt(parts[1]);
      const year2 = parseInt(parts[2]);

      if (
        !isNaN(month2) &&
        !isNaN(day2) &&
        !isNaN(year2) &&
        year2 > 1900 &&
        month2 <= 12
      ) {
        return `${year2}-${String(month2).padStart(2, "0")}-${String(day2).padStart(2, "0")}`;
      }
    }

    return dateString;
  };

  // Set refer_code in formData when component mounts or referCode changes
  useEffect(() => {
    if (referCode) {
      setFormData((prev) => ({
        ...prev,
        refer_code: referCode,
      }));
    }
  }, [referCode]);

  // Sync saved payment method from localStorage
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem("selectedPaymentMethod");
      if (saved) {
        setSavedPaymentMethod(saved);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    handleStorageChange();

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Fetch countries and districts on mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await landingApi.getCountries(authToken);
        const list = Array.isArray(res) ? res : res?.data || res?.countries || [];
        setNationality(list);
      } catch (error) {
        console.error("Error fetching countries:", error);
        setNationality([]);
      }
    };

    const fetchDistricts = async () => {
      try {
        const data = await landingApi.getDistricts();
        const list = Array.isArray(data) ? data : data?.data || data?.districts || [];
        setDistricts(list);
      } catch (error) {
        console.error("Error fetching districts:", error);
        setDistricts([]);
      }
    };

    fetchDistricts();
    fetchCountries();
  }, [authToken]);

  // Fetch townships when district changes
  useEffect(() => {
    const fetchTownships = async () => {
      if (!formData.district_id) {
        setTownships([]);
        return;
      }

      try {
        const data = await landingApi.getTownships(formData.district_id);
        const list = Array.isArray(data) ? data : data?.data || data?.townships || [];
        setTownships(list);
        setFormData((prev) => ({ ...prev, township_id: "" }));
      } catch (error) {
        console.error("Error fetching townships:", error);
        setTownships([]);
      }
    };

    fetchTownships();
  }, [formData.district_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      const numericValue = value.replace(/\D/g, "");
      if (numericValue.length <= 11) {
        setFormData({
          ...formData,
          [name]: numericValue,
        });
      }
      return;
    }

    if (name === "date_of_birth") {
      setFormData({
        ...formData,
        [name]: value,
      });
      return;
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData({
      ...loginData,
      [name]: value,
    });
  };

  // Handle existing user login
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const payload = {
        phone: loginData.phone,
        password: loginData.password,
      };

      console.log("Login payload:", payload);

      const response = await landingApi.loginWithPhone(authToken, payload);
      console.log("Login response:", response);

      if (response && response.user) {
        setUserData(response.user);
        setIsLoggedIn(true);
        setError("");

        const formattedDate = formatDateForInput(response.user.date_of_birth);

        setFormData({
          username: response.user.username || "",
          email: response.user.email || "",
          phone: response.user.phone || "",
          gender: response.user.gender || "",
          township_id: response.user.township_id || "",
          district_id: response.user.district_id || "",
          nationality: response.user.nationality_id || "",
          password: "",
          password_confirmation: "",
          date_of_birth: formattedDate || "",
          refer_code: referCode || "",
        });

        // Store the login password for verification
        localStorage.setItem("userLoginPassword", loginData.password);

        if (response.token) {
          localStorage.setItem("customerToken", response.token);
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      if (error.response) {
        const errorMessage =
          error.response.data?.message ||
          error.response.data?.errors ||
          error.message ||
          "Login failed. Please check your phone number and password.";
        setError(errorMessage);
      } else if (error.request) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError(error.message || "Login failed");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle membership selection
  const handleSelectMembership = (plan) => {
    setSelectedMembership(plan);
    setMembershipError("");
    setPasswordError("");
    setShowPasswordConfirm(false);
    setConfirmPassword("");
  };

  // Handle confirm membership with password verification
  const handleConfirmMembership = () => {
    if (!selectedMembership) {
      setMembershipError("Please select a membership plan.");
      return;
    }

    // Show password confirmation popup
    setShowPasswordConfirm(true);
    setPasswordError("");
    setConfirmPassword("");
  };

  // Verify password and proceed to payment
  const handleVerifyPassword = async () => {
    if (!confirmPassword) {
      setPasswordError("Please enter your password.");
      return;
    }

    // Get the stored login password
    const storedPassword = localStorage.getItem("userLoginPassword");

    if (confirmPassword !== storedPassword) {
      setPasswordError("Incorrect password. Please try again.");
      return;
    }

    // Password is correct, proceed with payment
    setIsProcessingPayment(true);
    setPasswordError("");

    try {
      // Store membership data in localStorage for payment
      localStorage.setItem(
        "membershipPlan",
        JSON.stringify({
          planId: selectedMembership.id,
          planName: selectedMembership.name,
          price: selectedMembership.price,
          discount: selectedMembership.discount,
        }),
      );

      // Close password modal
      setShowPasswordConfirm(false);
      setIsProcessingPayment(false);
      setIsMembershipModalOpen(false);

      // Navigate to payment page with membership data
      navigate("/paymentTransitions", {
        state: {
          membership: selectedMembership,
          userData: formData,
          amount: selectedMembership.price,
          planName: selectedMembership.name,
          paymentMethod: savedPaymentMethod,
        },
      });
    } catch (error) {
      console.error("Membership confirmation error:", error);
      setMembershipError("Failed to process membership. Please try again.");
      setIsProcessingPayment(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    // Password validation (only for new registration)
    if (!isLoggedIn && formData.password !== formData.password_confirmation) {
      setError(t("passwordsDoNotMatch") || "Passwords do not match");
      setIsLoading(false);
      return;
    }

    // Prepare payload
    const payload = {
      name: formData.username,
      email: formData.email,
      phone: formData.phone,
      gender: formData.gender ? Number(formData.gender) : null,
      township_id: formData.township_id ? Number(formData.township_id) : null,
      district_id: formData.district_id ? Number(formData.district_id) : null,
      nationality: formData.nationality ? Number(formData.nationality) : null,
      password: formData.password,
      confirm_password: formData.password_confirmation,
      date_of_birth: formData.date_of_birth || null,
      refer_code: formData.refer_code || null,
    };

    try {
      console.log("Submitting payload:", payload);

      const response = await landingApi.clientRegister(authToken, payload);
      console.log("Registration response:", response);

      const responseData = response?.data || response || {};

      if (responseData) {
        localStorage.setItem("registrationData", JSON.stringify(responseData));
      }

      navigate("/customerRegistrationSuccess", {
        state: {
          userData: responseData.user || responseData,
          referCode: formData.refer_code,
        },
      });
    } catch (error) {
      console.error("Registration error:", error);

      if (error.response) {
        const errorMessage =
          error.response.data?.message ||
          error.response.data?.errors ||
          error.message ||
          t("networkError") ||
          "Registration failed";
        setError(errorMessage);
      } else if (error.request) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError(error.message || t("networkError") || "Registration failed");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Switch to new registration
  const switchToNew = () => {
    setActiveTab("new");
    setIsLoggedIn(false);
    setUserData(null);
    setError("");
    setLoginData({ phone: "", password: "" });
    setSelectedMembership(null);
    setMembershipError("");
    setShowPasswordConfirm(false);
    setConfirmPassword("");
    setPasswordError("");
    setIsMembershipModalOpen(false);
    setFormData({
      username: "",
      email: "",
      phone: "",
      gender: "",
      township_id: "",
      district_id: "",
      nationality: "",
      password: "",
      password_confirmation: "",
      date_of_birth: "",
      refer_code: referCode || "",
    });
  };

  // Switch to existing user login
  const switchToExisting = () => {
    setActiveTab("existing");
    setIsLoggedIn(false);
    setUserData(null);
    setError("");
    setSelectedMembership(null);
    setMembershipError("");
    setShowPasswordConfirm(false);
    setConfirmPassword("");
    setPasswordError("");
    setIsMembershipModalOpen(false);
    setFormData({
      username: "",
      email: "",
      phone: "",
      gender: "",
      township_id: "",
      district_id: "",
      nationality: "",
      password: "",
      password_confirmation: "",
      date_of_birth: "",
      refer_code: referCode || "",
    });
  };

  // Check if form should be disabled (existing user logged in)
  const isFormDisabled = isLoggedIn;

  // Open membership modal
  const handleOpenMembershipModal = () => {
    setIsMembershipModalOpen(true);
    setMembershipError("");
    setSelectedMembership(null);
  };

  // Close membership modal
  const handleCloseMembershipModal = () => {
    setIsMembershipModalOpen(false);
    setSelectedMembership(null);
    setMembershipError("");
    setPasswordError("");
    setShowPasswordConfirm(false);
    setConfirmPassword("");
  };

  return (
    <section id="register">
      <div className="flex flex-1 flex-col lg:flex-row mx-4 lg:mx-10 gap-6 lg:gap-20 xl:gap-40">
        <div className="flex-1 hidden lg:block">
          <div className="flex-1 flex m-10 my-36 bg-primary-color rounded-3xl shadow-md">
            <div className="px-10 py-40 text-white space-y-5">
              <h1 className="text-3xl font-bold">Start Your Journey</h1>
              <p className="text-xl">
                Sign up now to explore the best deals on Zayverse.
              </p>
            </div>
          </div>
        </div>
        <div className="flex-1 flex">
          <div className="flex flex-col lg:px-10 lg:py-5 gap-0 flex-1 lg:border rounded-2xl lg:my-20 my-4 lg:shadow-md lg:bg-gray-100 mx-2 lg:mx-0">
            <h1 className="text-2xl lg:text-3xl font-bold text-center my-4 lg:my-3 mx-3">
              Customer Registration
            </h1>

            {/* Tab Buttons */}
            <div className="flex rounded-lg overflow-hidden mx-4 lg:mx-3 mb-4 border border-gray-300">
              <button
                className={`flex-1 py-2 text-center font-medium transition-colors ${
                  activeTab === "new"
                    ? "bg-primary-color text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
                onClick={switchToNew}
              >
                New Register
              </button>
              <button
                className={`flex-1 py-2 text-center font-medium transition-colors ${
                  activeTab === "existing"
                    ? "bg-primary-color text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
                onClick={switchToExisting}
              >
                Already Registered
              </button>
            </div>

            {/* Display Referral Code field if it exists */}
            {referCode && activeTab === "new" && !isLoggedIn && (
              <div className="flex flex-col text-sm lg:text-base px-4 lg:px-3">
                <span className="py-1 flex-1 flex text-green-600 font-semibold">
                  Referral Code
                </span>
                <input
                  type="text"
                  name="refer_code"
                  value={formData.refer_code}
                  disabled
                  className="px-3 py-2 outline-none flex-1 rounded-md border lg:border-none border-gray-500 bg-gray-100 cursor-not-allowed"
                />
                <span className="text-xs text-gray-500 mt-1">
                  This referral code has been automatically applied
                </span>
              </div>
            )}

            {/* Login Form for Existing Users */}
            {activeTab === "existing" && !isLoggedIn && (
              <form onSubmit={handleLogin}>
                <div className="flex flex-col text-sm lg:text-base px-4 lg:px-3">
                  <span className="py-1 flex-1 flex">Phone Number</span>
                  <input
                    type="tel"
                    name="phone"
                    value={loginData.phone}
                    onChange={handleLoginChange}
                    className="px-3 py-2 outline-none flex-1 rounded-md border lg:border-none border-gray-500"
                    placeholder="Enter your phone number"
                    required
                  />
                </div>

                <div className="flex flex-col text-sm lg:text-base px-4 lg:px-3">
                  <span className="py-1 flex-1 flex">Password</span>
                  <input
                    type="password"
                    name="password"
                    value={loginData.password}
                    onChange={handleLoginChange}
                    className="px-3 py-2 outline-none flex-1 rounded-md border lg:border-none border-gray-500"
                    placeholder="Enter your password"
                    required
                    minLength={8}
                  />
                </div>

                <div className="flex flex-1 justify-center my-5 text-sm lg:text-base">
                  <button
                    className="text-center custom-darkblue-button justify-center text-white flex flex-1 lg:mx-8 mx-5"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center">
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        {t("processing") || "Processing..."}
                      </span>
                    ) : (
                      "Login"
                    )}
                  </button>
                </div>

                {error && (
                  <div className="text-red-500 text-center whitespace-pre-line bg-red-50 p-3 rounded mx-4 lg:mx-3">
                    {error}
                  </div>
                )}
              </form>
            )}

            {/* Registration Form - Show for New Register OR Existing User Logged In */}
            {(activeTab === "new" || isLoggedIn) && (
              <form onSubmit={handleSubmit}>
                {/* Show success message when existing user is logged in */}
                {isLoggedIn && userData && (
                  <div className="mx-4 lg:mx-3 mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-green-700 text-sm">
                      Welcome back, {userData.username}! Your information has
                      been auto-filled.
                    </p>
                  </div>
                )}

                <div className="flex flex-col text-sm lg:text-base px-4 lg:px-3">
                  <span className="py-1 flex-1 flex">User Name</span>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    ref={usernameRef}
                    className={`px-3 py-2 outline-none flex-1 rounded-md border lg:border-none border-gray-500 ${
                      isFormDisabled ? "bg-gray-100 cursor-not-allowed" : ""
                    }`}
                    required
                    disabled={isFormDisabled}
                  />
                  {formData.username && !isFormDisabled && (
                    <RemoveBtn
                      setValue={(value) =>
                        setFormData((prev) => ({ ...prev, username: value }))
                      }
                      inputRef={usernameRef}
                    />
                  )}
                </div>

                <div className="flex flex-col text-sm lg:text-base px-4 lg:px-3">
                  <span className="py-1 flex-1 flex">Email Address</span>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    ref={emailRef}
                    className={`px-3 py-2 outline-none flex-1 rounded-md border lg:border-none border-gray-500 ${
                      isFormDisabled ? "bg-gray-100 cursor-not-allowed" : ""
                    }`}
                    required
                    disabled={isFormDisabled}
                  />
                  {formData.email && !isFormDisabled && (
                    <RemoveBtn
                      setValue={(value) =>
                        setFormData((prev) => ({ ...prev, email: value }))
                      }
                      inputRef={emailRef}
                    />
                  )}
                </div>

                <div className="flex flex-col text-sm lg:text-base px-4 lg:px-3">
                  <span className="py-1 flex-1 flex">Phone Number</span>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    ref={phoneRef}
                    className={`px-3 py-2 outline-none flex-1 rounded-md border lg:border-none border-gray-500 ${
                      isFormDisabled ? "bg-gray-100 cursor-not-allowed" : ""
                    }`}
                    required
                    disabled={isFormDisabled}
                  />
                  {formData.phone && !isFormDisabled && (
                    <RemoveBtn
                      setValue={(value) =>
                        setFormData((prev) => ({ ...prev, phone: value }))
                      }
                      inputRef={phoneRef}
                    />
                  )}
                </div>

                <div className="flex flex-col text-sm lg:text-base px-4 lg:px-3">
                  <span className="py-1 flex-1 flex">Gender</span>
                  <select
                    name="gender"
                    onChange={handleChange}
                    value={formData.gender}
                    className={`px-3 py-2 outline-none cursor-pointer border lg:border-none border-gray-500 rounded-md ${
                      isFormDisabled ? "bg-gray-100 cursor-not-allowed" : ""
                    }`}
                    disabled={isFormDisabled}
                  >
                    <option value="" disabled>
                      Please select your Gender
                    </option>
                    {gender.map((gdr) => (
                      <option value={gdr.id} key={gdr.id}>
                        {gdr.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col text-sm lg:text-base px-4 lg:px-3">
                  <span className="py-1 flex-1 flex cursor-pointer">
                    Date of Birth
                  </span>
                  <input
                    type="date"
                    name="date_of_birth"
                    onChange={handleChange}
                    value={formData.date_of_birth}
                    className={`border lg:border-none border-gray-500 rounded-md px-3 py-2 outline-none cursor-pointer ${
                      isFormDisabled ? "bg-gray-100 cursor-not-allowed" : ""
                    }`}
                    disabled={isFormDisabled}
                  />
                </div>

                <div className="flex flex-col text-sm lg:text-base px-4 lg:px-3">
                  <span className="py-1 flex-1 flex">Nationality</span>
                  <select
                    name="nationality"
                    onChange={handleChange}
                    value={formData.nationality}
                    className={`px-3 py-2 outline-none cursor-pointer border lg:border-none border-gray-500 rounded-md ${
                      isFormDisabled ? "bg-gray-100 cursor-not-allowed" : ""
                    }`}
                    disabled={isFormDisabled}
                  >
                    <option value="" disabled>
                      Please select your Nationality
                    </option>
                    {nationality.map((country) => (
                      <option value={country.id} key={country.id}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.nationality && (
                  <div className="flex flex-col px-4 lg:px-3">
                    <span className="py-1 flex-1 flex">Division</span>
                    <select
                      name="district_id"
                      onChange={handleChange}
                      value={formData.district_id}
                      className={`px-3 py-2 outline-none cursor-pointer border lg:border-none border-gray-500 rounded-md ${
                        isFormDisabled ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                      disabled={isFormDisabled}
                    >
                      <option value="" disabled>
                        Please select your division
                      </option>
                      {districts.map((district) => (
                        <option value={district.id} key={district.id}>
                          {district.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {formData.district_id && (
                  <div className="flex flex-col px-4 lg:px-3">
                    <span className="py-1 flex-1 flex">Township</span>
                    <select
                      name="township_id"
                      onChange={handleChange}
                      value={formData.township_id}
                      className={`px-3 py-2 outline-none cursor-pointer border lg:border-none border-gray-500 rounded-md ${
                        isFormDisabled ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                      disabled={isFormDisabled}
                    >
                      <option value="" disabled>
                        Please select your township
                      </option>
                      {townships.map((town) => (
                        <option value={town.id} key={town.id}>
                          {town.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Password fields - only show for new registration, not for existing users */}
                {!isLoggedIn && (
                  <>
                    <div className="flex flex-col text-sm lg:text-base px-4 lg:px-3">
                      <span className="py-1 flex-1 flex">Password</span>
                      <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        ref={passwordRef}
                        className="px-3 py-2 outline-none flex-1 rounded-md border lg:border-none border-gray-500"
                        required
                        minLength={8}
                      />
                      {formData.password && (
                        <RemoveBtn
                          setValue={(value) =>
                            setFormData((prev) => ({
                              ...prev,
                              password: value,
                            }))
                          }
                          inputRef={passwordRef}
                        />
                      )}
                    </div>

                    {formData.password && (
                      <div className="flex flex-col px-4 lg:px-3">
                        <span className="py-1 flex-1 flex">
                          Confirm Password
                        </span>
                        <input
                          type="password"
                          name="password_confirmation"
                          value={formData.password_confirmation}
                          onChange={handleChange}
                          ref={passwordConfirmRef}
                          className="px-3 py-2 outline-none flex-1 rounded-md border lg:border-none border-gray-500"
                          required
                        />
                        {formData.password_confirmation && (
                          <RemoveBtn
                            setValue={(value) =>
                              setFormData((prev) => ({
                                ...prev,
                                password_confirmation: value,
                              }))
                            }
                            inputRef={passwordConfirmRef}
                          />
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* Button Section - Different for New Register vs Existing User */}
                {isLoggedIn ? (
                  // Existing User: Show "Become a Member" button only
                  <div className="flex flex-col px-4 lg:px-3 mt-3">
                    <button
                      type="button"
                      onClick={handleOpenMembershipModal}
                      className="custom-darkblue-button custom-shadow-effect w-full flex items-center justify-center gap-2"
                    >
                      <span>⭐</span>
                      Become a Member
                    </button>
                    <p className="text-xs text-gray-500 mt-1 text-center">
                      Invest 30,000 MMK/month to enjoy 5% - 10% off at partner
                      shops
                    </p>
                  </div>
                ) : (
                  // New Register: Show "Update Profile" button only (for new registration)
                  <div className="flex flex-1 justify-center my-5 text-sm lg:text-base">
                    <button
                      className="text-center custom-darkblue-button justify-center text-white flex flex-1 lg:mx-8 mx-5"
                      type="submit"
                      disabled={isLoading || isFormDisabled}
                    >
                      {isLoading ? (
                        <span className="flex items-center justify-center">
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          {t("processing") || "Processing..."}
                        </span>
                      ) : (
                        t("register") || "Register"
                      )}
                    </button>
                  </div>
                )}

                {error && (
                  <div className="text-red-500 text-center whitespace-pre-line bg-red-50 p-3 rounded mx-4 lg:mx-3">
                    {error}
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Membership Modal - Only for Existing Users */}
      {isMembershipModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <span className="text-2xl">⭐</span> Become a Member
              </h2>
              <button
                onClick={handleCloseMembershipModal}
                className="p-2 transition-colors rounded-lg hover:bg-gray-100"
              >
                <svg
                  className="w-5 h-5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {/* Description */}
              <div className="mb-4 p-3 bg-gray-100 border border-gray-200 rounded-lg">
                <p className="text-sm text-gray-500">
                  Invest <strong>30,000 MMK</strong> a month to become a member
                  to enjoy membership discount at our partner shops from{" "}
                  <strong>(5% - 10% off)</strong> at anytime. Annual
                  subscription or Semi-annual subscription available start{" "}
                  <strong>NOW</strong>.
                </p>
                {/* <a
                  href="#"
                  className="text-sm text-blue-600 hover:underline mt-1 inline-block"
                >
                  Learn more about Partner Shops →
                </a> */}
              </div>

              {/* Membership Plans with Radio Buttons */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {membershipPlans.map((plan) => (
                  <label
                    key={plan.id}
                    className={`border-2 rounded-lg p-4 cursor-pointer shadow-md transition-all ${
                      selectedMembership?.id === plan.id
                        ? " bg-primary-color"
                        : "border-gray-200 hover:border-yellow-300"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="membership"
                        value={plan.id}
                        checked={selectedMembership?.id === plan.id}
                        onChange={() => handleSelectMembership(plan)}
                        className="mt-1 w-4 h-4 accent-secondary-color"
                      />
                      <div className="flex-1">
                        <h4 className="font-bold text-secondary-color">
                          {plan.name}
                        </h4>
                        <p className="text-lg font-bold text-green-500">
                          {plan.price.toLocaleString()} MMK
                        </p>
                        <div className="mt-1 space-y-1 text-sm text-gray-500">
                          <p>
                            🎯 Discount rate: <strong>{plan.discount}</strong>
                          </p>
                          <p>
                            📅 Valid: {plan.validFrom} – {plan.validTo}
                          </p>
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              {/* Payment Method Section */}
              {selectedMembership && (
                <>
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 mb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Payment method</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="font-medium text-gray-800">
                            💳 <br />
                            {savedPaymentMethod === "international"
                              ? "Visa/ Master"
                              : savedPaymentMethod === "mobile"
                                ? "MMQR"
                                : savedPaymentMethod}
                          </span>
                          {savedPaymentMethod === "international" && (
                            <span className="text-sm text-gray-500">
                              xxxxxx
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">Amount</p>
                        <p className="text-xl font-bold text-green-500">
                          {selectedMembership.price.toLocaleString()} MMK
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        navigate("/paymentTransitions", {
                          state: {
                            membership: selectedMembership,
                            userData: formData,
                            amount: selectedMembership.price,
                            planName: selectedMembership.name,
                            paymentMethod: savedPaymentMethod,
                            changePayment: true,
                          },
                        });
                      }}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Change Card
                    </button>
                  </div>

                  {/* Cancel and Confirm Buttons */}
                  <div className="flex gap-3 justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedMembership(null);
                        setMembershipError("");
                      }}
                      className="custom-bk-button custom-black-button"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmMembership}
                      disabled={!selectedMembership || isProcessingPayment}
                      className=" custom-darkblue-button custom-shadow-effect"
                    >
                      {isProcessingPayment ? (
                        <>
                          <svg
                            className="animate-spin h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Processing...
                        </>
                      ) : (
                        "Confirm"
                      )}
                    </button>
                  </div>

                  {membershipError && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                      {membershipError}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Password Confirmation Modal */}
      {showPasswordConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-[19px] font-bold text-gray-900">
                🔒 Confirm Password
              </h2>
              <button
                onClick={() => {
                  setShowPasswordConfirm(false);
                  setConfirmPassword("");
                  setPasswordError("");
                }}
                className="p-2 transition-colors rounded-lg hover:bg-gray-100"
              >
                <svg
                  className="w-5 h-5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Please enter your password to confirm your membership purchase.
              </p>

              <div className="flex flex-col">
                <span className="py-1 flex-1 flex text-sm font-medium text-gray-700">
                  Password
                </span>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="px-3 py-2 outline-none flex-1 rounded-md border border-gray-300 "
                  placeholder="Enter your password"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleVerifyPassword();
                    }
                  }}
                />
              </div>

              {passwordError && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {passwordError}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowPasswordConfirm(false);
                  setConfirmPassword("");
                  setPasswordError("");
                }}
                className=" custom-bk-button custom-shadow-effect"
              >
                Cancel
              </button>
              <button
                onClick={handleVerifyPassword}
                disabled={isProcessingPayment}
                className=" custom-darkblue-button custom-shadow-effect"
              >
                {isProcessingPayment ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Verifying...
                  </>
                ) : (
                  "Confirm"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default CustomerRegistration;
